import type Phaser from "phaser";

/**
 * @yjsgames/phaser-fx — 도메인 무관 Phaser 일회성 시각 이펙트 헬퍼.
 *
 * 모든 함수는 `scene`을 인자로 받아 GameObject를 생성하고 tween 후 자동 destroy한다(fire-and-forget).
 * 도메인 지식(폰트·색상 의미·텍스트 내용)은 전부 호출자가 인자/옵션으로 주입한다 —
 * "데미지 팝업"이 아니라 "떠오르며 사라지는 텍스트"처럼 일반화된 이름/시그니처(utility-class-generalize).
 *
 * 테스트는 scene mock(add 호출 + tweens.add 기록)으로 생성·tween 설정값을 검증한다(렌더 자체는 Phaser 런타임).
 */

/**
 * Text 풀(선택) — 제공 시 create/destroy 대신 acquire/release로 Text를 재사용한다(GC churn 제거).
 * 구조적 인터페이스라 패키지가 @yjsgames/object-pool에 의존하지 않는다(호출자가 ObjectPool<Text>를 주입).
 * 데미지·크리티컬 텍스트처럼 매 충돌마다 뜨는 고빈도 팝업에 유효.
 */
export interface FloatingTextPool {
  acquire(): Phaser.GameObjects.Text;
  release(text: Phaser.GameObjects.Text): void;
}

/** 떠오르며 페이드아웃하는 짧은 텍스트 (데미지 팝업 등). */
export interface FloatingTextOptions {
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly color: string;
  readonly fontFamily: string;
  readonly fontSize?: string;
  readonly depth?: number;
  /** 위로 떠오르는 거리(px). 기본 20. */
  readonly riseBy?: number;
  readonly duration?: number;
  /** 제공 시 Text를 풀에서 재사용(create/destroy 대신 acquire/release). 미제공 시 기존 fire-and-forget. */
  readonly pool?: FloatingTextPool;
}

export function floatingText(
  scene: Phaser.Scene,
  opts: FloatingTextOptions,
): Phaser.GameObjects.Text {
  const style = {
    fontFamily: opts.fontFamily,
    fontSize: opts.fontSize ?? "11px",
    color: opts.color,
  };
  let txt: Phaser.GameObjects.Text;
  if (opts.pool) {
    // 풀 재사용 — 직전 사용의 stale(텍스트·스타일·위치·alpha·tween)을 전부 재설정.
    txt = opts.pool.acquire();
    scene.tweens.killTweensOf(txt);
    txt
      .setText(opts.text)
      .setStyle(style)
      .setPosition(opts.x, opts.y)
      .setOrigin(0.5)
      .setDepth(opts.depth ?? 20)
      .setAlpha(1)
      .setScale(1)
      .setActive(true)
      .setVisible(true);
  } else {
    txt = scene.add.text(opts.x, opts.y, opts.text, style).setOrigin(0.5).setDepth(opts.depth ?? 20);
  }
  const pool = opts.pool;
  scene.tweens.add({
    targets: txt,
    y: opts.y - (opts.riseBy ?? 20),
    alpha: 0,
    duration: opts.duration ?? 420,
    onComplete: () => (pool ? pool.release(txt) : txt.destroy()),
  });
  return txt;
}

/** 화면 중앙 등에서 크게 떠오르며 페이드하는 알림 토스트 (외곽선 포함). */
export interface CenterToastOptions {
  readonly x: number;
  readonly y: number;
  readonly message: string;
  readonly fontFamily: string;
  readonly color?: string;
  readonly fontSize?: string;
  readonly stroke?: string;
  readonly strokeThickness?: number;
  readonly depth?: number;
  readonly riseBy?: number;
  readonly duration?: number;
}

export function centerToast(
  scene: Phaser.Scene,
  opts: CenterToastOptions,
): Phaser.GameObjects.Text {
  const txt = scene.add
    .text(opts.x, opts.y, opts.message, {
      fontFamily: opts.fontFamily,
      fontSize: opts.fontSize ?? "20px",
      color: opts.color ?? "#ffcf6b",
      stroke: opts.stroke ?? "#11121a",
      strokeThickness: opts.strokeThickness ?? 4,
      align: "center",
    })
    .setOrigin(0.5)
    .setDepth(opts.depth ?? 40);
  scene.tweens.add({
    targets: txt,
    y: opts.y - (opts.riseBy ?? 30),
    alpha: 0,
    duration: opts.duration ?? 1200,
    ease: "Quad.easeOut",
    onComplete: () => txt.destroy(),
  });
  return txt;
}

/** 타격 스파크 — 텍스처 이미지를 살짝 키우며 페이드아웃. */
export interface SparkOptions {
  readonly depth?: number;
  readonly startScale?: number;
  readonly endScale?: number;
  readonly alpha?: number;
  readonly duration?: number;
  /** 틴트 색(0xRRGGBB) — 지정 시 스파크를 해당 색으로 물들인다(미지정 시 텍스처 원색 유지, 예: 크리티컬 강조). */
  readonly tint?: number;
}

export function spark(
  scene: Phaser.Scene,
  x: number,
  y: number,
  textureKey: string,
  opts: SparkOptions = {},
): Phaser.GameObjects.Image {
  const img = scene.add
    .image(x, y, textureKey)
    .setDepth(opts.depth ?? 15)
    .setAlpha(opts.alpha ?? 0.9)
    .setScale(opts.startScale ?? 0.5);
  if (opts.tint !== undefined) img.setTint(opts.tint);
  scene.tweens.add({
    targets: img,
    alpha: 0,
    scale: opts.endScale ?? 1.2,
    duration: opts.duration ?? 160,
    onComplete: () => img.destroy(),
  });
  return img;
}

/** 폭발 링 — 반투명 원이 커지며 페이드아웃. */
export interface ExplosionRingOptions {
  readonly color?: number;
  readonly alpha?: number;
  readonly depth?: number;
  readonly endScale?: number;
  readonly duration?: number;
}

export function explosionRing(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  opts: ExplosionRingOptions = {},
): Phaser.GameObjects.Arc {
  const ring = scene.add
    .circle(x, y, radius, opts.color ?? 0xff7a3c, opts.alpha ?? 0.28)
    .setDepth(opts.depth ?? 14);
  scene.tweens.add({
    targets: ring,
    alpha: 0,
    scale: opts.endScale ?? 1.25,
    duration: opts.duration ?? 280,
    onComplete: () => ring.destroy(),
  });
  return ring;
}

/** 연쇄 빔 — 두 점을 잇는 선이 페이드아웃 (연쇄 공격 등). */
export interface ChainBeamOptions {
  readonly color?: number;
  readonly alpha?: number;
  readonly depth?: number;
  readonly lineWidth?: number;
  readonly duration?: number;
}

export function chainBeam(
  scene: Phaser.Scene,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  opts: ChainBeamOptions = {},
): Phaser.GameObjects.Line {
  const line = scene.add
    .line(0, 0, x1, y1, x2, y2, opts.color ?? 0x5ff0ff, opts.alpha ?? 0.85)
    .setOrigin(0, 0)
    .setDepth(opts.depth ?? 14);
  line.setLineWidth(opts.lineWidth ?? 1.5);
  scene.tweens.add({
    targets: line,
    alpha: 0,
    duration: opts.duration ?? 220,
    onComplete: () => line.destroy(),
  });
  return line;
}

/** 발사 섬광 — 텍스처가 커지며 페이드아웃 (muzzle flash). */
export interface MuzzleFlashOptions {
  readonly depth?: number;
  readonly alpha?: number;
  readonly startScale?: number;
  readonly endScale?: number;
  readonly duration?: number;
  /** 초기 회전각(rad). 같은 텍스처라도 발사마다 다른 각도로 터뜨려 단조로움을 줄인다. 기본 0. */
  readonly rotation?: number;
}

export function muzzleFlash(
  scene: Phaser.Scene,
  x: number,
  y: number,
  textureKey: string,
  opts: MuzzleFlashOptions = {},
): Phaser.GameObjects.Image {
  const img = scene.add
    .image(x, y, textureKey)
    .setDepth(opts.depth ?? 10)
    .setAlpha(opts.alpha ?? 0.9)
    .setScale(opts.startScale ?? 1)
    .setRotation(opts.rotation ?? 0);
  scene.tweens.add({
    targets: img,
    alpha: 0,
    scale: opts.endScale ?? 1.5,
    duration: opts.duration ?? 120,
    onComplete: () => img.destroy(),
  });
  return img;
}

/** 파편 옵션 — 호출자가 텍스처(1px 등 단색)·tint·개수를 주입한다 (도메인 무관). */
export interface DebrisOptions {
  readonly count?: number;
  /** 파편 색 (예: 피격 벽돌의 현재 tint). 미지정 시 텍스처 원색. */
  readonly tint?: number;
  readonly depth?: number;
  /** 방사 거리 px — 파편별 0.6~1.0배 랜덤. */
  readonly speed?: number;
  /** 낙하 추가 px — 살짝 떨어지는 중력감. */
  readonly gravity?: number;
  /** 파편 한 변 scale (1px 텍스처 기준 px). */
  readonly size?: number;
  readonly duration?: number;
  readonly alpha?: number;
  /** 난수 소스 (기본 Math.random) — 테스트 결정론 주입용. */
  readonly rng?: () => number;
}

/** 파편 — 작은 조각 N개가 방사+낙하하며 페이드 (피격/파괴 타격감, fire-and-forget). */
export function debris(
  scene: Phaser.Scene,
  x: number,
  y: number,
  textureKey: string,
  opts: DebrisOptions = {},
): Phaser.GameObjects.Image[] {
  const rng = opts.rng ?? Math.random;
  const count = opts.count ?? 5;
  const speed = opts.speed ?? 26;
  const size = opts.size ?? 3;
  const out: Phaser.GameObjects.Image[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = rng() * Math.PI * 2;
    const dist = speed * (0.6 + 0.4 * rng());
    const img = scene.add
      .image(x, y, textureKey)
      .setDepth(opts.depth ?? 15)
      .setAlpha(opts.alpha ?? 0.9)
      .setScale(size);
    if (opts.tint !== undefined) (img as { setTint?: (t: number) => unknown }).setTint?.(opts.tint);
    scene.tweens.add({
      targets: img,
      x: x + Math.cos(angle) * dist,
      y: y + Math.sin(angle) * dist + (opts.gravity ?? 10),
      alpha: 0,
      scale: size * 0.4,
      duration: (opts.duration ?? 240) * (0.8 + 0.4 * rng()),
      ease: "Quad.easeOut",
      onComplete: () => img.destroy(),
    });
    out.push(img);
  }
  return out;
}

/** 보상 획득 fly — 아이콘(+수량 라벨)을 시작점에서 잠깐 띄웠다가 목표(재화 칩/탭)로 축소·이동·페이드. */
export interface FlyToTargetOptions {
  readonly startX: number;
  readonly startY: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly iconKey: string;
  readonly iconSize?: number;
  /** 아이콘 아래 수량 텍스트(예: "+1.2K", "유물!"). 미지정 시 아이콘만. */
  readonly label?: string;
  readonly labelColor?: string;
  readonly fontFamily?: string;
  readonly depth?: number;
  /** 시작점에서 떠올라 표시되는 시간(ms). 기본 360. */
  readonly holdMs?: number;
  /** 목표로 날아가는 시간(ms). 기본 540. */
  readonly flyMs?: number;
  /** 아이콘 개수(보상 양이 클수록 ↑). 2 이상이면 hold에서 원형으로 퍼졌다가 fly에서 수렴. 수량 텍스트는 1개. 기본 1. */
  readonly iconCount?: number;
  /** 아이콘 퍼짐 반경(px, iconCount>1). 기본 13. */
  readonly scatterRadius?: number;
}

export function flyToTarget(scene: Phaser.Scene, opts: FlyToTargetOptions): void {
  const size = opts.iconSize ?? 24;
  const count = Math.max(1, Math.floor(opts.iconCount ?? 1));
  const scatterR = opts.scatterRadius ?? 13;
  const holdMs = opts.holdMs ?? 360;
  const flyMs = opts.flyMs ?? 540;
  const cont = scene.add.container(opts.startX, opts.startY).setDepth(opts.depth ?? 3001);

  // 아이콘 N개 — 모두 중심(0,0)에서 시작. count>1이면 hold에서 원형으로 퍼졌다가 fly에서 수렴.
  const icons: Phaser.GameObjects.Image[] = [];
  for (let i = 0; i < count; i += 1) {
    const ic = scene.add.image(0, 0, opts.iconKey).setDisplaySize(size, size);
    cont.add(ic);
    icons.push(ic);
  }
  // 수량 텍스트는 1개(총합) — 아이콘 그룹 아래 중앙.
  if (opts.label) {
    const txt = scene.add
      .text(0, size * 0.78, opts.label, {
        fontFamily: opts.fontFamily ?? "sans-serif",
        fontSize: "14px",
        color: opts.labelColor ?? "#ffffff",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0.5);
    cont.add(txt);
  }
  cont.setScale(0.5);

  // 1단계 hold: 잠깐 커지며 살짝 떠오름 + 아이콘들이 원형으로 퍼짐(count>1).
  if (count > 1) {
    icons.forEach((ic, i) => {
      const ang = (i / count) * Math.PI * 2 - Math.PI / 2;
      scene.tweens.add({
        targets: ic,
        x: Math.cos(ang) * scatterR,
        y: Math.sin(ang) * scatterR,
        duration: holdMs,
        ease: "Back.easeOut",
      });
    });
  }
  scene.tweens.add({
    targets: cont,
    scale: 1.15,
    y: opts.startY - 16,
    duration: holdMs,
    ease: "Back.easeOut",
    onComplete: () => {
      // 2단계 fly: 아이콘 수렴(offset→0) + Container가 목표(재화 칩/탭)로 축소·이동·페이드.
      if (count > 1) {
        icons.forEach((ic) => {
          scene.tweens.add({ targets: ic, x: 0, y: 0, duration: flyMs, ease: "Cubic.easeIn" });
        });
      }
      scene.tweens.add({
        targets: cont,
        x: opts.targetX,
        y: opts.targetY,
        scale: 0.3,
        alpha: 0.25,
        duration: flyMs,
        ease: "Cubic.easeIn",
        onComplete: () => cont.destroy(),
      });
    },
  });
}
